import {useForm} from 'react-hook-form'
import {BadgeDetailsContext} from '../../context'
import {useContext, useState, useEffect, useRef} from 'react'
import {CheckBox} from '../../../../components/CheckBox'
import ProductSelectModal from '../modals/ProductSelectModal'
import {MultiSelect} from '../../../../components/MultiSelect/'
import InputColourPicker from '../../../../components/InputColourPicker'
import {MultiSelectTreeDropDown} from '../../../../components/MultiSelectTreeDropDown'
import {InputText} from '../../../../components/InputText'
import Loading from '../../../loading'

interface BadgeDetailsFormProps {
  initialValues?: any
  isEdit: boolean
}

const BadgeDetailsForm = ({isEdit}: BadgeDetailsFormProps) => {
  const {
    brands,
    categories,
    badgeDetails,
    isLoading,
    addProductBadge,
    updateProductBadge,
    isOperationLoading,
    hasWritePermission,
  } = useContext(BadgeDetailsContext)
  const [showProductModal, setShowProductModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasLoadedInitialData = useRef(false)

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: {errors, dirtyFields},
    setError,
  } = useForm({
    defaultValues: {
      badgeName: '',
      tagLabel: '',
      brandIds: [] as any,
      categoryIds: [],
      individual_products: [],
      bgColor: '#FFFFFF',
      fontColor: '#000000',
      productListing: null,
      productDetails: null,
    },
  })

  const selectedBrands = brands
    .filter((brand: any) => watch('brandIds')?.includes(brand.id))
    .map((brand: any) => ({
      label: brand.name,
      value: brand.id,
    }))
  // Refill form in edit mode when badgeDetails is loaded (only once)
  useEffect(() => {
    if (isEdit && badgeDetails && badgeDetails.id && !hasLoadedInitialData.current) {
      reset({
        badgeName: badgeDetails.badgeName || '',
        tagLabel: badgeDetails.tagLabel || '',
        brandIds: badgeDetails.brandIds || [],
        categoryIds: badgeDetails.categoryIds || [],
        individual_products: badgeDetails.individual_products || [],
        bgColor: badgeDetails.bgColor || '#FFFFFF',
        fontColor: badgeDetails.fontColor || '#000000',
        productListing: badgeDetails.productListing,
        productDetails: badgeDetails.productDetails,
      })
      hasLoadedInitialData.current = true // Mark as loaded
    }
  }, [isEdit, badgeDetails, reset])

  const selectedProducts = watch('individual_products') || []
  const backgroundColor = watch('bgColor')
  const fontColor = watch('fontColor')

  const handleProductsSelected = (products: any) => {
    setValue('individual_products', products, {shouldDirty: true})
  }

  const onSubmit = handleSubmit(async (data) => {
    setIsSubmitting(true)
    if (isEdit) {
      updateProductBadge(data, badgeDetails.id, dirtyFields, setError)
    } else {
      addProductBadge(data, setError)
    }
    setIsSubmitting(false)
  })

  return (
    <form className='form position-relative ' onSubmit={onSubmit}>
      {/* Section 1: Badge Information and Style */}
      <div className='mw-800px'>
        <h3 className='mb-8'>Badge Information and Style</h3>
        <div className='mb-8'>
          <div className='mb-8'>
            <InputText
              id='name'
              label='Product Badge'
              placeholder='Enter product badge'
              isRequired
              maxLength={100}
              error={errors.badgeName}
              register={register('badgeName', {required: true, maxLength: 100})}
              className='mb-2'
              disabled={!hasWritePermission}
            />
            <div className='form-text'>
              Enter product badge details or describe the usage of the badge.
            </div>
            {errors.badgeName && (
              <div className='text-danger'>Product Badge is required (max 100 chars).</div>
            )}
          </div>
          <div className='mb-8  mw-500px'>
            <InputText
              id='tagLabel'
              label='Display Name'
              placeholder='Enter display name'
              isRequired
              maxLength={20}
              error={errors.tagLabel}
              register={register('tagLabel', {required: true, maxLength: 20})}
              className='mb-2'
              disabled={!hasWritePermission}
            />
            <div className='form-text'>The entered value will be displayed with the product.</div>
            {errors.tagLabel && (
              <div className='text-danger'>Display Name is required (max 20 chars).</div>
            )}
          </div>
        </div>
        <div>
          <div className='mb-10'>
            <label className='form-label mb-5'>Choose Badge Style</label>
            <div className='d-flex gap-15'>
              <InputColourPicker
                id='bgColor'
                name='bgColor'
                label='Background Color'
                value={backgroundColor}
                register={register('bgColor')}
                error={errors.bgColor}
                isHexOnly={true}
                disabled={!hasWritePermission}
              />
              <InputColourPicker
                id='fontColor'
                name='fontColor'
                label='Font Color'
                value={fontColor}
                register={register('fontColor')}
                error={errors.fontColor}
                isHexOnly={true}
                disabled={!hasWritePermission}
              />
            </div>
          </div>
          <div className='mb-10'>
            <label className='form-label mb-5'>Badge Visibility</label>
            <div className=' mb-5'>
              <CheckBox
                id='showOnProductCard'
                name='productListing'
                label='Show a badge on the product card'
                checked={!!watch('productListing')}
                onChange={(e: any) =>
                  setValue('productListing', e.target.checked, {shouldDirty: true})
                }
                error={errors.productListing}
                disabled={!hasWritePermission}
              />
              <div className='form-text'>
                If this is checked, a badge will be displayed on the product listing.
              </div>
            </div>
            <div className=''>
              <CheckBox
                id='showOnProductDetail'
                name='productDetails'
                label='Show a badge on the product detail page'
                checked={!!watch('productDetails')}
                onChange={(e: any) =>
                  setValue('productDetails', e.target.checked, {shouldDirty: true})
                }
                error={errors.productDetails}
                disabled={!hasWritePermission}
              />
              <div className='form-text'>
                If this is enabled, a badge will be displayed on the product detail page.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Product Selection */}
      <div className='mw-800px'>
        <h3 className='mb-8'>Product Selection</h3>
        <div>
          <div className='mb-8'>
            <MultiSelectTreeDropDown
              id='categories'
              label='Choose Products by Category'
              treeData={categories}
              labelKey='name'
              valueKey='id'
              placeholder='Select Categories'
              showTags={true}
              defaultValue={isEdit ? badgeDetails?.categoryIds || [] : []}
              onSubmit={(categories: any) =>
                setValue('categoryIds', categories, {shouldDirty: true})
              }
              error={errors.categoryIds}
              isDisableExpand={false}
              isDefaultExpandAll={true}
              isClearable={true}
              isSearch={true}
              {...(isOperationLoading && !isEdit
                ? {selectedValues: watch('categoryIds')}
                : isEdit && badgeDetails?.categoryIds?.length > 0
                ? {selectedValues: badgeDetails?.categoryIds}
                : {})}
              isDisable={!hasWritePermission}
            />
          </div>
          <div className='mb-8 mw-200px'>
            <MultiSelect
              id='brands'
              label='Choose Brand'
              options={brands}
              labelKey='name'
              valueKey='id'
              valueType='number'
              placeholder='Select Brands'
              handleSelected={(values: any) =>
                setValue(
                  'brandIds',
                  values?.map((v: any) => v.value),
                  {shouldDirty: true}
                )
              }
              error={errors.brandIds}
              disabled={!hasWritePermission}
              {...(isOperationLoading && !isEdit
                ? {selectedValues: selectedBrands}
                : isEdit && selectedBrands?.length > 0
                ? {selectedValues: selectedBrands}
                : {})}
            />
          </div>
        </div>
        <div>
          <div className='mb-8'>
            <label className='form-label'>Choose a Product</label>
            <div className='d-flex gap-3 align-items-center'>
              <button
                type='button'
                className='btn btn-light-primary'
                onClick={() => setShowProductModal(true)}
                disabled={!hasWritePermission}
              >
                Select Products
              </button>
              {selectedProducts?.length > 0 && !isOperationLoading && (
                <span
                  className='text-muted text-decoration-underline cursor-pointer'
                  onClick={() => setShowProductModal(true)}
                  style={{cursor: 'pointer'}}
                >
                  {selectedProducts.length} products selected
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <button
          type='submit'
          className='btn btn-primary'
          disabled={
            isSubmitting ||
            isOperationLoading ||
            Object.keys(dirtyFields).length === 0 ||
            !hasWritePermission
          }
        >
          {isSubmitting || isOperationLoading ? (
            <>
              Saving...
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              ></span>
            </>
          ) : (
            <>Save Badge</>
          )}
        </button>
      </div>

      {isLoading && <Loading />}

      {showProductModal && (
        <ProductSelectModal
          onClose={() => setShowProductModal(false)}
          selectedProducts={selectedProducts}
          onProductsSelected={handleProductsSelected}
        />
      )}
    </form>
  )
}

export default BadgeDetailsForm
